import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Users, MessageCircle, TrendingUp, Plus, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import AppointmentDialog from "@/components/AppointmentDialog";

export default function Dashboard() {
  const { user } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [showApptDialog, setShowApptDialog] = useState(false);

  const load = async () => {
    const [a, p] = await Promise.all([api.get("/appointments"), api.get("/patients")]);
    setAppointments(a.data);
    setPatients(p.data);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const upcoming = appointments
    .filter((a) => new Date(a.start) >= new Date())
    .slice(0, 5);

  const todayCount = appointments.filter((a) => {
    const d = new Date(a.start);
    const t = new Date();
    return d.toDateString() === t.toDateString();
  }).length;

  const stats = [
    { label: "Citas hoy", value: todayCount, icon: Calendar, color: "#B5704A" },
    { label: "Citas próximas", value: upcoming.length, icon: TrendingUp, color: "#9C5E3D" },
    { label: "Clientas", value: patients.length, icon: Users, color: "#8C5A3A" },
    { label: "Estado WhatsApp", value: user?.whatsapp_connected ? "Conectado" : "Inactivo", icon: MessageCircle, color: "#6F5A4C", isText: true },
  ];

  return (
    <div className="p-8 lg:p-12 max-w-7xl">
      <header className="flex items-end justify-between mb-12">
        <div>
          <span className="label-eyebrow text-[#B5704A]">Resumen</span>
          <h1 className="font-display text-3xl sm:text-4xl font-[350] text-[#3A2418] mt-2">
            Hola, <span className="italic font-[700]">{user?.name?.split(" ")[0]}</span>
          </h1>
          <p className="text-[#6F5A4C] mt-2 text-sm">Aquí tienes tu vista del día.</p>
        </div>
        <Button
          onClick={() => setShowApptDialog(true)}
          className="bg-[#B5704A] hover:bg-[#9C5E3D] hover:text-white text-white rounded-full"
          data-testid="dashboard-new-appointment"
        >
          <Plus className="w-4 h-4 mr-2" /> Nueva cita
        </Button>
      </header>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
        {stats.map((s) => (
          <div key={s.label} className="surface-card surface-card-hover p-6" data-testid={`stat-${s.label}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="label-eyebrow text-[#6F5A4C] text-[10px]">{s.label}</div>
                <div className="font-display text-3xl font-[600] text-[#3A2418] mt-3">
                  {s.value}
                </div>
              </div>
              <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: `${s.color}15` }}>
                <s.icon className="w-4 h-4" style={{ color: s.color }} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Upcoming + Quick links */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 surface-card p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display text-xl font-[600] text-[#3A2418]">Próximas citas</h2>
            <Link to="/app/calendario" className="text-sm text-[#B5704A] hover:text-[#9C5E3D] flex items-center gap-1" data-testid="link-to-calendar">
              Ver todo <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          {upcoming.length === 0 ? (
            <div className="text-center py-12 text-[#6F5A4C]">
              <Calendar className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p>No tienes citas próximas.</p>
              <Button
                onClick={() => setShowApptDialog(true)}
                variant="ghost"
                className="mt-3 text-[#B5704A] hover:bg-[#E8DFD2]/30"
              >
                Crear primera cita
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {upcoming.map((a) => {
                const d = new Date(a.start);
                return (
                  <div key={a.id} className="flex items-center gap-4 p-3 rounded-md hover:bg-[#F4EFE4] transition-colors" data-testid={`appt-${a.id}`}>
                    <div className="w-14 text-center">
                      <div className="text-xs text-[#6F5A4C] uppercase">{d.toLocaleDateString("es", { month: "short" })}</div>
                      <div className="font-display text-xl font-[700] text-[#3A2418]">{d.getDate()}</div>
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-[#3A2418]">{a.title}</div>
                      <div className="text-xs text-[#6F5A4C]">
                        {d.toLocaleTimeString("es", { hour: "2-digit", minute: "2-digit" })} · {a.location || "Sin ubicación"}
                      </div>
                    </div>
                    {a.google_event_id && (
                      <span className="text-[10px] uppercase tracking-wider text-[#8C5A3A] font-medium">Sync</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="surface-card p-6">
          <h2 className="font-display text-xl font-[600] text-[#3A2418] mb-6">Accesos rápidos</h2>
          <div className="space-y-3">
            <Link to="/app/pacientes" className="block p-4 rounded-md bg-[#E8DFD2]/30 hover:bg-[#E8DFD2]/50 transition-colors" data-testid="quick-patients">
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-[#B5704A]" />
                <div>
                  <div className="font-medium text-[#3A2418]">Pacientes</div>
                  <div className="text-xs text-[#6F5A4C]">Gestiona historias clínicas</div>
                </div>
              </div>
            </Link>
            <Link to="/app/configuracion" className="block p-4 rounded-md bg-[#F4EFE4] hover:bg-[#E8DFD2]/30 transition-colors" data-testid="quick-settings">
              <div className="flex items-center gap-3">
                <MessageCircle className="w-5 h-5 text-[#B5704A]" />
                <div>
                  <div className="font-medium text-[#3A2418]">Integraciones</div>
                  <div className="text-xs text-[#6F5A4C]">Calendar y WhatsApp</div>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </div>

      <AppointmentDialog open={showApptDialog} onClose={() => setShowApptDialog(false)} onSaved={load} patients={patients} />
    </div>
  );
}
